<template>
  <header>
    <Menubar class="w-full">
      <template #start>
        <div class="logo">
          <RouterLink to="/">
            <img alt="Brussels Airlines" class="logo" src="@/assets/brusselsairlines-sign.svg" width="50" height="50" />
          </RouterLink>
          <h1>Allotment Management Suite</h1>
        </div>
      </template>
      <template #end>
        <div class="user">
          <Avatar class="user-avatar shrink-0" :label="initials" size="xlarge" shape="circle" />
          <div>
            <div>{{ fullName }}</div>
            <div class="user-actions">
              <div class="app-switcher" v-if="hasCharterAccess">
                <button type="button" class="app-switcher-button" :class="{ active: !isCharterApp }" @click="switchApp('allotment')">Allotment</button>
                <button v-if="hasCharterAccess" type="button" class="app-switcher-button" :class="{ active: isCharterApp }" @click="switchApp('charter')">Charter</button>
              </div>
              <p-tag v-if="userRole" class="mr-2">{{ userRole }}</p-tag>
              <a role="button" class="button" style="font-size: 0.75rem" @click="logOut">logout</a>
            </div>
          </div>
        </div>
      </template>
    </Menubar>
  </header>
  <div class="main-wrapper">
    <SideNavigation />
    <main class="main-content">
      <RouterView />
    </main>
  </div>
  <p-toast>
    <template #message="slotProps">
      <span
        :class="{
          pi: true,
          'pi-times-circle': slotProps.message.severity === 'error',
          'pi-exclamation-triangle': slotProps.message.severity === 'warn',
          'pi-info-circle': slotProps.message.severity === 'info',
          'pi-check': slotProps.message.severity === 'success',
        }"
        style="margin-top: 0.125rem"
      />
      <div class="p-toast-message-text">
        <span class="p-toast-summary">{{ slotProps.message.summary }}</span>
        <div class="p-toast-detail" v-html="slotProps.message.detail" />
      </div>
    </template>
  </p-toast>
  <p-confirm-dialog />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, RouterView, useRouter } from 'vue-router';
import Avatar from 'primevue/avatar';
import Menubar from 'primevue/menubar';
import SideNavigation from '@/components/structure/SideNavigation/SideNavigation.vue';
import { signOut } from '@/app/auth/auth-services.ts';
import { useAllotmentsStore } from '@/stores/';
import {useCurrentUserStore} from "@/stores/current-user-store.ts";
import {storeToRefs} from "pinia";
import {useCurrentAppStore} from "@/stores/current-app-store.ts";

const allotmentsStore = useAllotmentsStore();
const currentUserStore = useCurrentUserStore();
const currentAppStore = useCurrentAppStore();
const router = useRouter();
const { currentUser, hasCharterAccess } = storeToRefs(currentUserStore);
const { isCharterApp } = storeToRefs(currentAppStore);

const initials = computed(() => currentUser?.value ? `${currentUser.value.firstName?.charAt(0)}${currentUser.value.lastName?.charAt(0)}` : '--');
const fullName = computed(() => currentUser?.value ? `${currentUser.value.firstName} ${currentUser.value.lastName}` : '');
const userRole = computed(() => currentUser?.value ? allotmentsStore.getRoleById(Number(currentUser.value.allotmentRoleId)) : '');

function logOut() {
  signOut();
}

function switchApp(app: 'allotment' | 'charter') {
  currentAppStore.setCurrentApp(app);

  router.push({
    name: app === 'charter' ? 'charter-flights-overview' : 'home-redirect',
  });
}
</script>

<style lang="scss" scoped>
.app-switch {
  align-items: center;
  display: inline-flex;
  gap: 0.3125rem;
  margin-right: 0.625rem;
  white-space: nowrap;
}

.user {
  font-size: 1rem;
  padding: 0 1.25rem;
  display: flex;
  align-items: center;
  line-height: 1;

  .user-actions {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex-wrap: wrap;
  }

  .app-switcher {
    display: inline-flex;
    border: 1px solid var(--c_grey--medium);
    border-radius: 61.875rem;
    overflow: hidden;

    &-button {
      background: transparent;
      border: 0;
      color: var(--c_primary);
      cursor: pointer;
      font-size: 0.75rem;
      line-height: 1;
      padding: 0.25rem 0.5rem;

      &.active {
        background: var(--c_primary);
        color: var(--c_white);
      }
    }
  }

  &-avatar {
    margin-right: 0.46875rem;
  }

  small {
    margin-left: 0.3125rem;
  }

  .button {
    cursor: pointer;
  }
}

.main-wrapper {
  display: flex;
  width: 100%;
  background: var(--c_grey--1);
  margin-top: 5rem;
  height: calc(100vh - 5rem);
  font-size: var(--fz);

  @media (max-width: 59.99em) {
    margin-top: 3.125rem;
    height: calc(100vh - 3.125rem);
  }

  .main-content {
    background: var(--c_white);
    flex: 1;
    display: flex;
    flex-direction: column;
    margin: 1.25rem;
    padding: 0 1.25rem;
    overflow-y: auto;

    > :not(h1, h2, h3, h4, h5, h6):first-child {
      margin-top: 1.25rem;
    }
  }
}
</style>
